import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, Award, Flame } from 'lucide-react';
import { getHabits, getLogs, calculateStreak } from '../lib/storage';

const StatsOverview = () => {
  const [stats, setStats] = useState({
    totalHabits: 0,
    bestStreak: 0,
    completionRate: 0,
    totalCompletions: 0,
  });

  useEffect(() => {
    calculateStats();
  }, []);

  const calculateStats = async () => {
    try {
      const habits = await getHabits();
      const logs = await getLogs();

      // Total habits
      const totalHabits = habits.length;

      // Best streak
      let bestStreak = 0;
      habits.forEach((habit) => {
        const streak = calculateStreak(habit.id, logs);
        if (streak > bestStreak) bestStreak = streak;
      });

      // Completion rate (last 7 days)
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const recentLogs = logs.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= sevenDaysAgo && logDate <= today;
      });

      const possibleCompletions = totalHabits * 7;
      const completionRate = possibleCompletions > 0 
        ? Math.round((recentLogs.length / possibleCompletions) * 100)
        : 0;

      // Total completions
      const totalCompletions = logs.length;

      setStats({
        totalHabits,
        bestStreak,
        completionRate,
        totalCompletions,
      });
    } catch (error) {
      console.error('Error calculating stats:', error);
    }
  };

  const statCards = [
    {
      label: 'Active Habits',
      value: stats.totalHabits,
      icon: Target,
      gradient: 'from-blue-500 to-cyan-500',
      testId: 'stat-total-habits',
    },
    {
      label: 'Best Streak',
      value: `${stats.bestStreak} days`,
      icon: Flame,
      gradient: 'from-orange-500 to-red-500',
      testId: 'stat-best-streak',
    },
    {
      label: 'Completion Rate',
      value: `${stats.completionRate}%`,
      icon: TrendingUp,
      gradient: 'from-green-500 to-emerald-500',
      testId: 'stat-completion-rate',
    },
    {
      label: 'Total Check-ins',
      value: stats.totalCompletions,
      icon: Award,
      gradient: 'from-purple-500 to-pink-500',
      testId: 'stat-total-completions',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-overview">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            data-testid={stat.testId}
            className="p-5 gradient-card rounded-2xl border border-white/20 dark:border-white/10 hover:shadow-xl transition-all duration-300 hover-glow"
          >
            <div className="flex items-start justify-between mb-3">
              <div 
                className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${stat.gradient} shadow-lg`}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">
                {stat.value}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {stat.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsOverview;
